import { Box as LayoutBox } from '~/components/Layout/Box'
import TradeForm from '~/components/TradeForm'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { TradeStatus } from '~/components/TradeForm/TradeStatus'
import { updateValue, useWETHContext } from '../../context'
import BigNumber from 'bignumber.js'
import { useTokenPrice } from '~/hooks/useTokenPrice'
import { AddressZero } from '@ethersproject/constants'
import { ethers } from 'ethers'
import { Trans, useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { StyledFailureInfo, StyledResultBox } from './styled'
import { Column, Row } from '~/components/Layout'
import { useRouter } from 'next/router'
import { useBreakpoint, useWETHRouter, useSpotSwapRouter } from '~/hooks'
import { formatFormAmount } from '~/utils/numbers'

export const Result = () => {
  const { isMobile } = useBreakpoint()
  const { state } = useWETHContext()
  const { receipt, isWrap } = state
  const status = useMemo(() => receipt?.status, [receipt])
  const isOpen = useModalOpened(ApplicationModal.WETH_RESULT)
  const toggleResultModal = useModalToggle(ApplicationModal.WETH_RESULT)
  const toggleWrapModal = useModalToggle(ApplicationModal.WETH_WRAP)
  const { t } = useTranslation()
  const title = isWrap ? t('wrap_eth') : t('unwrap_eth')
  const isWETHRouter = useWETHRouter()
  const isSpotSwapRouter = useSpotSwapRouter()

  const onBack = () => {
    if (isWETHRouter) return toggleWrapModal()

    toggleResultModal()
  }

  const needPadding = isWETHRouter || (isMobile && !isSpotSwapRouter)

  return (
    <StyledResultBox
      $pad={needPadding}
      visible={isOpen}
      title={title}
      onBack={onBack}
    >
      <TradeForm.Body>
        {status ? <SuccessResult /> : <FailureResult />}
      </TradeForm.Body>
    </StyledResultBox>
  )
}

const FailureResult = () => {
  const { t } = useTranslation()
  const { dispatch } = useWETHContext()
  const toggleWrapModal = useModalToggle(ApplicationModal.WETH_WRAP)
  const toggleReviewModal = useModalToggle(ApplicationModal.WETH_PREVIEW)
  const toggleResultModal = useModalToggle(ApplicationModal.WETH_RESULT)
  const { pathname } = useRouter()
  const isWETHRouter = pathname === '/weth'
  const onRetry = () => {
    toggleReviewModal()
  }
  const onCancel = () => {
    dispatch(updateValue(''))
    if (isWETHRouter) return toggleWrapModal()

    toggleResultModal()
  }
  return (
    <>
      <TradeForm.Status status={TradeStatus.Failed}>
        {t('exchange_failed')}
      </TradeForm.Status>
      <LayoutBox mt="-8px">
        <TradeForm.Divider dashed />
      </LayoutBox>
      <TradeForm.Stack>
        <StyledFailureInfo>
          <Trans i18nKey="wrap_failed_info">
            造成失败可能的原因：
            <ul>
              <li>网络拥堵情况波动较大，矿工费不足</li>
              <li>WETH 合约调用失败</li>
              <li>具体原因请查看交易哈希，请尝试重新进行封装</li>
            </ul>
          </Trans>
        </StyledFailureInfo>
      </TradeForm.Stack>
      <TradeForm.Spacer h={60} />
      <Row justify="space-between">
        <Column style={{ width: '48%' }}>
          <TradeForm.Button ghost onClick={onCancel}>
            {t('cancel')}
          </TradeForm.Button>
        </Column>
        <Column style={{ width: '48%' }}>
          <TradeForm.Button onClick={onRetry}>
            {t('try_again')}
          </TradeForm.Button>
        </Column>
      </Row>
    </>
  )
}
const SuccessResult = () => {
  const { state, dispatch } = useWETHContext()
  const { pathname } = useRouter()
  const { value, inputToken, outputToken, receipt } = state
  const toggleResultModal = useModalToggle(ApplicationModal.WETH_RESULT)
  const toggleWrapModal = useModalToggle(ApplicationModal.WETH_WRAP)
  const ethPrice = useTokenPrice(AddressZero)
  const gasFee = new BigNumber(
    ethers.utils.formatEther(receipt.effectiveGasPrice.mul(receipt.gasUsed)),
  )
  const { t } = useTranslation()
  const gasFeeEther = gasFee.sd(4).toFormat()
  const gasFeeUsd = gasFee.times(ethPrice).sd(4).toFormat(1)
  const title = t('exchange_success')
  const onConfirm = () => {
    dispatch(updateValue(''))
    toggleResultModal()

    if (pathname === '/weth') toggleWrapModal()
  }
  return (
    <>
      <TradeForm.Status status={TradeStatus.Success}>{title}</TradeForm.Status>
      <LayoutBox mt="-8px">
        <TradeForm.Divider dashed />
      </LayoutBox>
      <LayoutBox mb="10px">
        <TradeForm.TradeFlow
          flow={[
            { token: inputToken, amount: value },
            { token: outputToken, amount: value },
          ]}
        />
      </LayoutBox>
      <TradeForm.Stack>
        <TradeForm.TipRow title={t('rate')}>
          1 {inputToken?.symbol} = 1 {outputToken.symbol}
        </TradeForm.TipRow>
        <TradeForm.TipRow title={t('gas_fee')}>
          ${gasFeeUsd}({formatFormAmount(gasFeeEther, 3)} ETH)
        </TradeForm.TipRow>
        <TradeForm.TipRow title={t('trading_fee')}>$0(0)</TradeForm.TipRow>
        <TradeForm.TipRow title={t('network')}>
          <TradeForm.Network />
        </TradeForm.TipRow>
      </TradeForm.Stack>
      <TradeForm.Spacer h={60} />
      <TradeForm.Button onClick={onConfirm}>{t('done')}</TradeForm.Button>
    </>
  )
}
